import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { HeroBackdrop } from '#app/components/hero-backdrop.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { CardWave } from '#app/components/ui/card-wave.tsx'
import { EmptyState } from '#app/components/ui/empty-state.tsx'
import { Reveal } from '#app/components/ui/reveal.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'
import { useLang, useLocalizedPath } from '#app/utils/i18n.ts'
import { BackLink } from '../+marketing-components/back-link.tsx'
import { BlogPostCard, type PostWithRelations } from './blog-post-card.tsx'
import { Pagination } from './pagination.tsx'

const backToBlog = {
	de: 'Zurück zum Blog',
	fr: 'Retour au blog',
	en: 'Back to blog',
} as const

/**
 * Blog category and tag listing: the centred listing hero shared with the shop
 * category page, then the post grid in a full `container` so the cards match
 * `/blog` width. An empty listing links back to `/blog`.
 */
export function BlogListing({
	title,
	summary,
	empty,
	posts,
	currentPage,
	totalPages,
	basePath,
}: {
	title: string
	summary: string
	empty: { title: string; description: string }
	posts: PostWithRelations[]
	currentPage: number
	totalPages: number
	basePath: string
}) {
	const lang = useLang()
	const localePath = useLocalizedPath()
	const { t } = useTranslation()

	return (
		<>
			<section className="relative overflow-hidden pt-32 pb-24">
				<HeroBackdrop />

				<div className="relative z-10 container text-center">
					<Reveal>
						<BackLink to={localePath('/blog')} className="mb-6">
							{backToBlog[lang]}
						</BackLink>
					</Reveal>
					<h1 className="text-h2 md:text-h1 mx-auto max-w-3xl font-bold">
						<WordReveal text={title} wordClassName="text-gradient-brand" />
					</h1>
					<Reveal delay={100}>
						<p className="text-body-md text-muted-foreground md:text-body-lg mx-auto mt-4 max-w-2xl">
							{summary}
						</p>
					</Reveal>
				</div>
			</section>

			<div className="container pt-16 pb-16">
				{posts.length === 0 ? (
					<EmptyState
						icon="file-text"
						title={empty.title}
						description={empty.description}
						action={
							<Button asChild variant="outline" size="sm">
								<Link to={localePath('/blog')}>{t('blog.allPosts')}</Link>
							</Button>
						}
					/>
				) : (
					<>
						<CardWave className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
							{posts.map((post) => (
								<BlogPostCard key={post.id} post={post} lang={lang} />
							))}
						</CardWave>

						{totalPages > 1 && (
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								basePath={localePath(basePath)}
							/>
						)}
					</>
				)}
			</div>
		</>
	)
}
